import { Injectable } from '@angular/core';
import { WallpaperService, WallpaperSize, WallpaperStyle } from '../wallpaper.service';
import { HevcPicture, writeDynamicHeic } from './heic-container';
import { pictureFromStream, rgbaToI420 } from './hevc-bitstream';
import { desktopXmp, schedulePlist } from './desktop-metadata';
import { DayLightOptions, DynamicFrame, DynamicMode, DYNAMIC_MODES, applyLightTable, dayFrames, fileOrder, lightAndDark, lightTable } from './day-light';

export interface DynamicWallpaperOptions {
  style: WallpaperStyle;
  colors: string[];
  seed: number;
  params: number[];
  mode: DynamicMode;
  frames: number;
  light: DayLightOptions;
}

/** the HEVC quantizer: near transparent for smooth gradients (≈48 dB), a few hundred KB a 5K day */
const QP = 22;

/** the planes are BT.601 full range with sRGB primaries and transfer, as the HEIC's colr says */
const COLOR_SPACE = { primaries: 'bt709', transfer: 'iec61966-2-1', matrix: 'smpte170m', fullRange: true };

/**
 * The macOS dynamic wallpaper (2026-10-12): the wallpaper generator's picture drawn once per frame
 * of the day (day-light.ts relights the palette), every frame HEVC coded, and all of them written into
 * one .heic with the schedule macOS reads (desktop-metadata.ts, heic-container.ts).
 *
 * The encoder is the browser's own (WebCodecs; Safari and Chrome on a Mac use the system's HEVC
 * encoder): the page ships no codec of its own, so a browser without one cannot make the file.
 */
@Injectable( {
  providedIn: 'root',
} )
export class DynamicWallpaperService {

  constructor( private wallpaperService: WallpaperService ) {
  }

  frames( options: DynamicWallpaperOptions ): DynamicFrame[] {
    return dayFrames( options.mode, options.frames, options.colors, options.light );
  }

  /** the picture itself (the noon frame) onto `canvas` at its current size; every other frame is it, relit */
  renderBase( canvas: HTMLCanvasElement, options: DynamicWallpaperOptions, style = options.style ): void {
    this.wallpaperService.render( canvas, { style, colors: options.colors, seed: options.seed, params: style === options.style ? options.params : undefined } );
  }

  /** a frame's light on the pixels of the picture, in place (nothing at noon) */
  relightPixels( data: Uint8ClampedArray, options: DynamicWallpaperOptions, frame: DynamicFrame ): void {
    const table = this.tableFor( options, frame );
    if ( table ) applyLightTable( data, table );
  }

  /** one frame drawn onto `canvas` at its current size: the picture, then the hour's light on it */
  renderFrame( canvas: HTMLCanvasElement, options: DynamicWallpaperOptions, frame: DynamicFrame ): void {
    this.renderBase( canvas, options );
    const ctx = canvas.getContext( '2d', { willReadFrequently: true } as any ) as CanvasRenderingContext2D | null;
    if ( !ctx || !this.tableFor( options, frame ) ) return;
    const image = ctx.getImageData( 0, 0, canvas.width, canvas.height );
    this.relightPixels( image.data, options, frame );
    ctx.putImageData( image, 0, 0 );
  }

  /** the colour tables of the hours drawn lately (a slider move asks for the same ones again) */
  private tables = new Map<string, Uint8ClampedArray | null>();

  private tableFor( options: DynamicWallpaperOptions, frame: DynamicFrame ): Uint8ClampedArray | null {
    const key = `${ frame.altitude.toFixed( 3 ) }|${ frame.hour < 12 }|${ options.light.night }|${ options.light.glow }`;
    if ( !this.tables.has( key ) ) {
      if ( this.tables.size > 64 ) this.tables.clear();
      this.tables.set( key, lightTable( frame.altitude, frame.hour < 12, options.light ) );
    }
    return this.tables.get( key ) || null;
  }

  /** whether this browser can HEVC encode a picture of this size */
  async supports( size: WallpaperSize ): Promise<boolean> {
    return ( await nativeConfigs( size.width, size.height ) ).length > 0;
  }

  /** the whole day as a .heic, `progress` told after every coded frame */
  async build( options: DynamicWallpaperOptions, size: WallpaperSize, progress: ( done: number, total: number ) => void ): Promise<Blob> {
    const { width, height } = size;
    // every configuration the browser says it takes; the first frame finds the one that really works
    let configs = await nativeConfigs( width, height );
    if ( !configs.length ) throw new Error( 'This browser has no HEVC encoder for this size' );
    // the Light frame first: the picture macOS shows, and the one that carries the schedule
    const frames = fileOrder( this.frames( options ) );
    const canvas = document.createElement( 'canvas' );
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext( '2d', { willReadFrequently: true } as any ) as CanvasRenderingContext2D | null;
    if ( !ctx ) throw new Error( 'No 2D canvas' );
    const pictures: HevcPicture[] = [];
    progress( 0, frames.length );
    // the picture is drawn once; each frame is its pixels with that hour's light
    this.renderBase( canvas, options );
    const base = ctx.getImageData( 0, 0, width, height ).data;
    for ( const frame of frames ) {
      const pixels = new Uint8ClampedArray( base );
      this.relightPixels( pixels, options, frame );
      const i420 = rgbaToI420( pixels, width, height );
      const failures: string[] = [];
      let picture: HevcPicture | null = null;
      for ( const config of configs ) {
        try {
          const coded = await encodeNative( config, i420, width, height );
          picture = pictureFromStream( coded.bytes, coded.description, width, height );
          // the rest of the day goes the way that worked
          configs = [ config ];
          break;
        } catch ( e: any ) {
          failures.push( `${ describe( config ) }: ${ e?.message || e }` );
        }
      }
      if ( !picture ) throw new Error( failures.join( '; ' ) );
      pictures.push( picture );
      progress( pictures.length, frames.length );
    }
    const { light, dark } = lightAndDark( frames );
    const kind = ( DYNAMIC_MODES.find( m => m.key === options.mode ) || DYNAMIC_MODES[ 0 ] ).kind;
    const xmp = desktopXmp( kind, schedulePlist( kind, frames, light, dark ) );
    // the first picture is the primary one: Finder, the wallpaper list and any viewer without the schedule show it
    const file = writeDynamicHeic( pictures, light, xmp );
    return new Blob( [ file ], { type: 'image/heic' } );
  }
}

// ---------------------------------------------------------------- the browser's own encoder (WebCodecs)

/**
 * The configurations to try, in order: HEVC Main at the lowest level that holds the picture (5.1 to 4K, 6
 * above it), a fixed quantizer or a generous bitrate, Annex B or the "hvc" format; each one the browser
 * says it takes, as the browser returns it.
 */
async function nativeConfigs( width: number, height: number ): Promise<any[]> {
  const VideoEncoderCtor = ( globalThis as any ).VideoEncoder;
  if ( !VideoEncoderCtor?.isConfigSupported || !( globalThis as any ).VideoFrame ) {
    return [];
  }
  const level = width * height <= 8912896 ? 153 : width * height <= 35651584 ? 180 : 0;
  if ( !level ) return [];
  const base = { codec: `hvc1.1.6.L${ level }.B0`, width, height, framerate: 1, hardwareAcceleration: 'no-preference' };
  const candidates: any[] = [];
  for ( const format of [ 'annexb', 'hevc' ] ) {
    candidates.push( { ...base, hevc: { format }, bitrateMode: 'quantizer' } );
    candidates.push( { ...base, hevc: { format }, bitrate: Math.round( width * height * 1.5 ) } );
  }
  const supported: any[] = [];
  for ( const config of candidates ) {
    try {
      const support = await VideoEncoderCtor.isConfigSupported( config );
      if ( support?.supported ) supported.push( { ...config, ...( support.config || {} ) } );
    } catch {
      // not this one
    }
  }
  return supported;
}

/** a configuration in a few words, for the error a failed build shows */
function describe( config: any ): string {
  return `${ config.codec } ${ config.hevc?.format || '' } ${ config.bitrateMode === 'quantizer' ? 'quantizer' : 'bitrate' }`.replace( /\s+/g, ' ' );
}

/** one I420 picture through the browser's encoder: the coded bytes, and the decoder configuration if it gave one */
function encodeNative( config: any, i420: Uint8Array, width: number, height: number ): Promise<{ bytes: Uint8Array; description: Uint8Array | null }> {
  return new Promise( ( resolve, reject ) => {
    const chunks: Uint8Array[] = [];
    let description: Uint8Array | null = null;
    let failed: any = null;
    const encoder = new ( globalThis as any ).VideoEncoder( {
      output: ( chunk: any, metadata: any ) => {
        const bytes = new Uint8Array( chunk.byteLength );
        chunk.copyTo( bytes );
        chunks.push( bytes );
        const d = metadata?.decoderConfig?.description;
        if ( d ) {
          description = d instanceof ArrayBuffer ? new Uint8Array( d.slice( 0 ) ) : new Uint8Array( d.buffer.slice( d.byteOffset, d.byteOffset + d.byteLength ) );
        }
      },
      error: ( e: any ) => failed = e,
    } );
    try {
      encoder.configure( config );
      const frame = new ( globalThis as any ).VideoFrame( i420, {
        format: 'I420', codedWidth: width, codedHeight: height, timestamp: 0, colorSpace: COLOR_SPACE,
      } );
      const encodeOptions: any = { keyFrame: true };
      if ( config.bitrateMode === 'quantizer' ) encodeOptions.hevc = { quantizer: QP };
      encoder.encode( frame, encodeOptions );
      frame.close();
    } catch ( e ) {
      try { encoder.close(); } catch { /* closed */ }
      reject( e );
      return;
    }
    encoder.flush().then( () => {
      try { encoder.close(); } catch { /* closed */ }
      if ( failed ) {
        reject( failed );
        return;
      }
      const length = chunks.reduce( ( s, c ) => s + c.length, 0 );
      if ( !length ) {
        reject( new Error( 'The encoder wrote nothing' ) );
        return;
      }
      const out = new Uint8Array( length );
      let at = 0;
      for ( const c of chunks ) {
        out.set( c, at );
        at += c.length;
      }
      resolve( { bytes: out, description } );
    }, ( e: any ) => {
      try { encoder.close(); } catch { /* closed */ }
      reject( failed || e );
    } );
  } );
}
